import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFetch } from "utils/adminFetch";

function StatusBadge({ status }) {
  const s = String(status || "pending").toLowerCase();
  if (s === "approved") {
    return (
      <span className="inline-flex items-center rounded-md bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700 dark:bg-green-500/15 dark:text-green-300">
        Approved
      </span>
    );
  }
  if (s === "rejected") {
    return (
      <span className="inline-flex items-center rounded-md bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 dark:bg-red-500/15 dark:text-red-300">
        Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
      Pending
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function FxProClientAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("pending");
  const [actionId, setActionId] = useState(null);
  const [msg, setMsg] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/fxpro-clients");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load entries.");
        setRows([]);
        return;
      }
      setRows(data.rows || []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter(
      (r) => String(r.status || "pending").toLowerCase() === filter,
    );
  }, [rows, filter]);

  async function handleApprove(r) {
    if (!r?.id || actionId) return;
    setActionId(r.id);
    setError("");
    setMsg("");
    try {
      const res = await adminFetch(`/api/admin/fxpro-clients/${r.id}/approve`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Approve failed.");
        return;
      }
      setMsg(`Approved ${r.email}.`);
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setActionId(null);
    }
  }

  async function handleReject(r) {
    if (!r?.id || actionId) return;
    setActionId(r.id);
    setError("");
    setMsg("");
    try {
      const res = await adminFetch(`/api/admin/fxpro-clients/${r.id}/reject`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Reject failed.");
        return;
      }
      setMsg(`Rejected ${r.email}.`);
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setActionId(null);
    }
  }

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const r of rows) {
      const s = String(r.status || "pending").toLowerCase();
      if (c[s] != null) c[s] += 1;
    }
    return c;
  }, [rows]);

  return (
    <div className="mt-3 px-2">
      <h4 className="mb-2 text-2xl font-bold text-navy-700 dark:text-white">
        FxPro Clients Verifications
      </h4>
      <p className="mb-6 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
        Broker partner e-mails submitted by clients. Confirm registrations from
        your FxPro Partner dashboard, then click <strong>Approve</strong> to
        update the status both in the client account page and here.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[
          { id: "pending", label: `Pending (${counts.pending})` },
          { id: "approved", label: `Approved (${counts.approved})` },
          { id: "rejected", label: `Rejected (${counts.rejected})` },
          { id: "all", label: `All (${rows.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setFilter(tab.id)}
            className={
              filter === tab.id
                ? "rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white shadow"
                : "rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            }
          >
            {tab.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void loadRows()}
          disabled={loading}
          className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <p className="mb-3 text-sm font-medium text-red-500">{error}</p>
      ) : null}
      {msg ? (
        <p className="mb-3 text-sm font-medium text-green-600 dark:text-green-300">
          {msg}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-white/10">
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Client
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Partner-broker e-mail
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Status
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Submitted
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Approved
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-gray-500 dark:text-gray-400"
                >
                  Loading…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-gray-500 dark:text-gray-400"
                >
                  No entries yet. If the table does not exist, run{" "}
                  <code className="text-xs">
                    backend/sql/fxpro_clients_verifications.sql
                  </code>{" "}
                  in Supabase.
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-gray-100 align-top dark:border-white/5"
                >
                  <td className="px-4 py-3 text-navy-700 dark:text-white">
                    <div className="font-medium">
                      {r.clientFullName || `Client #${r.clientId}`}
                    </div>
                    {r.clientAccountEmail ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {r.clientAccountEmail}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-800 dark:text-gray-100">
                    {r.email}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {formatDate(r.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                    {formatDate(r.approvedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-nowrap items-center justify-end gap-2 whitespace-nowrap">
                      {(() => {
                        const s = String(r.status || "pending").toLowerCase();
                        const busy = actionId === r.id;
                        if (s === "approved") {
                          return (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void handleReject(r)}
                              className="shrink-0 rounded-md border border-amber-400/70 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-200 disabled:opacity-60 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200"
                            >
                              {busy ? "…" : "Revoke"}
                            </button>
                          );
                        }
                        return (
                          <>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void handleApprove(r)}
                              className="shrink-0 rounded-md bg-green-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-green-600 disabled:opacity-60"
                            >
                              {busy ? "Approving…" : "Approve"}
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void handleReject(r)}
                              className="shrink-0 rounded-md border border-red-400/70 bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:opacity-60 dark:border-red-500/40 dark:bg-red-500/15 dark:text-red-300"
                            >
                              {busy ? "…" : "Reject"}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
